import {
  type PatientConsentDocumentsResponse,
  PatientConsentDocumentsResponseSchema,
  type PatientConsentHistory,
  PatientConsentHistorySchema,
  type PatientConsentKind,
  PatientConsentKindSchema,
  PublishConsentDocumentRequestSchema,
  type PublishConsentDocumentResponse,
  PublishConsentDocumentResponseSchema,
} from "@emr/contracts";
import { z } from "zod";
import { SchedulingRequestError, schedulingRequestError } from "../demo/schedulingErrors";

/**
 * The clinic's consent documents in the query cache: the admin page, and the email and text
 * message notice staff read to a patient before recording "Patient agreed" (PA-8). Not audited.
 */
export const patientConsentDocumentsKey = (siteId: string) =>
  ["patient-consent-documents", siteId] as const;

/** What the admin editor sends; the contract normalizes line breaks and trims before it checks. */
export type PublishConsentDocumentInput = z.input<typeof PublishConsentDocumentRequestSchema>;

/**
 * Patient consent (PA-11) on the staff scheduling lane. Every route acts on the client's own
 * site (its `x-site-id`), so none takes a site argument. Errors are SchedulingRequestError, whose
 * `detail` names a stale publish (`consent_document_changed`).
 *
 * - GET  /v1/patients/:patientId/consents            the chart's history; the read is audited
 * - GET  /v1/patients/:patientId/consents?export=1   the same history for a download, audited
 *                                                    as an export
 * - GET  /v1/scheduling/consent-documents            the clinic's documents (any staff role)
 * - POST /v1/scheduling/consent-documents/:kind      publish a new version (clinic_admin)
 */
export function createPatientConsentsClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    fallbackMessage: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(path, body, signal);
    if (!response.ok) throw await schedulingRequestError(response, fallbackMessage);
    return schema.parse(await response.json());
  }
  const patientPath = (patientId: string) =>
    `/v1/patients/${encodeURIComponent(z.string().uuid().parse(patientId))}/consents`;

  return {
    /** The patient's consent decisions at this clinic and the exact text of each version. */
    async listPatientConsents(
      patientId: string,
      signal?: AbortSignal,
    ): Promise<PatientConsentHistory> {
      return json(
        patientPath(patientId),
        PatientConsentHistorySchema,
        "The consent records could not be loaded.",
        undefined,
        signal,
      );
    },

    /** The same history, read for a download so the export itself is audited. */
    async exportPatientConsents(
      patientId: string,
      signal?: AbortSignal,
    ): Promise<PatientConsentHistory> {
      return json(
        `${patientPath(patientId)}?export=1`,
        PatientConsentHistorySchema,
        "The consent export could not be prepared.",
        undefined,
        signal,
      );
    },

    async getPatientConsentDocuments(
      signal?: AbortSignal,
    ): Promise<PatientConsentDocumentsResponse> {
      return json(
        "/v1/scheduling/consent-documents",
        PatientConsentDocumentsResponseSchema,
        "The patient terms could not be loaded.",
        undefined,
        signal,
      );
    },

    /**
     * Publishes new text against `expectedVersion`. Text identical to the current version
     * publishes nothing (`published: false`).
     */
    async publishPatientConsentDocument(
      kind: PatientConsentKind,
      input: PublishConsentDocumentInput,
      signal?: AbortSignal,
    ): Promise<PublishConsentDocumentResponse> {
      return json(
        `/v1/scheduling/consent-documents/${PatientConsentKindSchema.parse(kind)}`,
        PublishConsentDocumentResponseSchema,
        "The new version could not be published.",
        PublishConsentDocumentRequestSchema.parse(input),
        signal,
      );
    },
  };
}

export type PatientConsentsClient = ReturnType<typeof createPatientConsentsClient>;

/** Someone published a newer version of the document first: reload before publishing. */
export function isStaleConsentDocument(error: unknown): boolean {
  return (
    error instanceof SchedulingRequestError &&
    (error.detail === "consent_document_changed" || error.status === 409)
  );
}

/** The signed-in role may not read or change this (401/403). */
export function isConsentAccessRefusal(error: unknown): boolean {
  return error instanceof SchedulingRequestError && (error.status === 401 || error.status === 403);
}
